import { Modal, Box, useTheme, Typography, Divider } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { useContext, useEffect } from 'react'
import RootStore from '~/stores/RootStore'
import Backdrop from '../Backdrop'
import AnimatedBox from '../Motion/AnimatedBox'
import layout from '~/theme/layout'
import { SearchInput } from '../SearchInput'
import { SearchResultsRow } from './SearchResultsRow'
import CardContainer from '../CardContainer'
import CardSectionTitle from '../CardSectionTitle'
import { screenSearchResult } from 'shared-solo/stores/SearchStore/SearchStore'
import { useI18n } from 'shared-lib/i18n/i18n'
import { AnimatePresence } from 'motion/react'
import { useEscapeKeyListener } from '~/lib/hooks/useEscapeKeyListener'
import { useKeyboardIsVisible } from '~/lib/hooks/useKeyboardIsVisible'
import { dvhSafeStyle } from '../../lib/polyFills'

export const SearchModal = observer(() => {
  const store = useContext(RootStore)
  const theme = useTheme()
  const { s } = useI18n()
  const { isKeyboardOpen } = useKeyboardIsVisible()

  const {
    searchVisible,
    showNoResults,
    _searchTerm,
    setSearchTerm,
    searchResults,
    dismissSearch,
    recentSearches,
  } = store.searchStore

  useEffect(() => {
    if (searchVisible) {
      setSearchTerm('')
    }
  }, [searchVisible, setSearchTerm])

  useEscapeKeyListener(dismissSearch, true)

  return (
    <AnimatePresence>
      {searchVisible && (
        <Modal
          key='search-modal'
          open={true}
          hideBackdrop
          disablePortal
          aria-labelledby='search-modal'
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <>
            <Backdrop onClose={dismissSearch} />
            <AnimatedBox
              entering={['fade', 'up']}
              exiting={['fade', 'down']}
              animate={{
                top: isKeyboardOpen ? theme.spacing(2) : 'auto',
                transition: {
                  duration: 0.5,
                  ease: 'easeOut',
                },
              }}
              sx={{
                width: layout.modalDrawerWidthSmall,
                ...dvhSafeStyle('60dvh'),
                touchAction: 'none',
                margin: theme.spacing(2),
                borderRadius: 2,
                backgroundColor: theme.palette.background.paper,
                boxShadow: theme.shadows[10],
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                padding: theme.spacing(4),
                alignItems: 'center',
                overflow: 'hidden',
                mt: 0,
                pt: 0,
                position: 'absolute',
              }}
            >
              <Box sx={{
                width: '100%',
                position: 'sticky',
                top: 0,
                zIndex: 1000,
                pt: 4,
                pb: 2,
              }}>
                <SearchInput
                  value={_searchTerm}
                  onChange={setSearchTerm}
                  placeholder={s('Search for clients, treatments, articles, etc.')}
                  autoFocus
                />
              </Box>

              <Box sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(2),
                overflowY: 'auto',
                overflowX: 'hidden',
                flex: 1,
              }}>
                {searchVisible && _searchTerm.length === 0 && (
                  <CardContainer sx={{ p: 0, pt: 1 }}>
                    <CardSectionTitle text={s('Recents')} />
                    <Divider variant='middle' />
                    {recentSearches.map((search: screenSearchResult, index: number) => (
                      <SearchResultsRow item={search} key={search.primaryTerm} isLast={index === recentSearches.length - 1} />
                    ))}
                    {recentSearches.length === 0 && (
                      <Typography variant='subtitle2' sx={{ textAlign: 'center', py: 2 }}>
                        {s('Your recent searches will appear here')}
                      </Typography>
                    )}
                  </CardContainer>
                )}

                {showNoResults && (
                  <Typography variant='subtitle2' sx={{ textAlign: 'center' }}>
                    {s('No results found')}
                  </Typography>
                )}

                {searchResults
                  .filter(section => section.loading || (!section.loading && section.data.length > 0))
                  .map((section) => (
                    <CardContainer key={section.title} sx={{ p: 0, pt: 1, overflow: 'visible' }}>
                      <CardSectionTitle text={section.title} />
                      <Divider variant='middle' />
                      {section.loading
                        ? (Array.from({ length: 3 }).map((_, index) => (
                          <SearchResultsRow loading key={`skeleton-${section.title}-${index}`} isLast={index === 2} />
                        )))
                        : (section.data.map((item, index) => (
                          <SearchResultsRow
                            item={item.item}
                            isLast={index === section.data.length - 1}
                            key={item.item.primaryTerm}
                          />
                        )))
                      }
                    </CardContainer>
                  ))}
              </Box>
            </AnimatedBox>
          </>
        </Modal>
      )}
    </AnimatePresence>
  )
})

export default SearchModal